import {
  Thought,
  ThoughtDimension,
  ThoughtDimensionAttributes,
  ThoughtDomain,
} from "@/generated/fastapi";
import {
  DIM_ATTR_CANONOICAL_LIST,
  getAssessmentQuestionForDimension,
  getDefaultAssessmentInstrumentId,
} from "@/shared/config/backend";
import { QuestionnaireDimensionOriented, QuestionnaireItem } from "./types";

export function getDimensionScore(
  thought: Thought,
  dimensionAttr: ThoughtDimensionAttributes,
) {
  const dimension = thought[dimensionAttr];
  if (dimension && dimension.score !== null && dimension.score !== undefined) {
    return dimension.score;
  }
  return 0;
}

export function getUnansweredDimensions(
  thought: Thought,
): ThoughtDimensionAttributes[] {
  return DIM_ATTR_CANONOICAL_LIST.filter((dim) => {
    const dimension = thought[dim as keyof Thought] as ThoughtDimension | null;
    return dimension === null || dimension?.score === null;
  });
}

export function getQuestionForDimension(
  dimension: ThoughtDimensionAttributes,
  domain: ThoughtDomain,
  instrumentId: string = getDefaultAssessmentInstrumentId(),
): QuestionnaireItem {
  const questionItem = getAssessmentQuestionForDimension({
    dimension,
    domain,
    instrumentId,
  });
  if (!questionItem) {
    return { dimension, question: `No question found for ${dimension}` };
  }

  return { dimension, question: questionItem.question };
}

export function buildQuestionnaireThoughtOriented(
  thought: Thought,
  instrumentId: string = getDefaultAssessmentInstrumentId(),
) {
  const unansweredDimensions = getUnansweredDimensions(thought);

  return {
    id: thought.id,
    mindstring: thought.mindstring,
    domain: thought.domain!,
    unansweredDimensions,
    dimensions: {
      dim0: thought.dim0 ?? null,
      dim1: thought.dim1 ?? null,
      dim2: thought.dim2 ?? null,
      dim3: thought.dim3 ?? null,
    },
    dimensionQuestions: unansweredDimensions.map((dim) =>
      getQuestionForDimension(dim, thought.domain!, instrumentId),
    ),
  };
}

export function buildQuestionnaireDimensionOriented(
  thoughts: Thought[],
  instrumentId: string = getDefaultAssessmentInstrumentId(),
): QuestionnaireDimensionOriented[] {
  if (!thoughts.length) return [];

  const domain = thoughts[0].domain;
  if (!domain) return [];

  const dimensionMap = new Map<
    ThoughtDimensionAttributes,
    QuestionnaireDimensionOriented
  >();

  DIM_ATTR_CANONOICAL_LIST.forEach((dim) => {
    const questionItem = getQuestionForDimension(dim, domain, instrumentId);

    const thoughtsForDimension = thoughts
      .filter((thought) => {
        const dimension = thought[
          dim as keyof Thought
        ] as ThoughtDimension | null;
        return dimension === null || dimension?.score === null;
      })
      .map((thought) => ({
        id: thought.id,
        mindstring: thought.mindstring,
        currentScore: getDimensionScore(thought, dim),
      }));

    if (thoughtsForDimension.length > 0) {
      dimensionMap.set(dim, {
        dimension: dim,
        question: questionItem.question,
        thoughts: thoughtsForDimension,
      });
    }
  });

  return Array.from(dimensionMap.values());
}
